import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSalesAgent,
  deleteSalesAgent,
  getSalespeople,
  setMyAvailability,
  updateSalesAgent,
} from '@/services/outbound-calls-service/salespeople.service';
import { OUTBOUND_POLL_INTERVAL_MS } from '@/services/outbound-calls-service/outbound-calls.constants';
import {
  AvailabilityStatus,
  SalesAgentPayload,
} from '@/services/outbound-calls-service/salespeople.service.types';
import { User } from '@/services/user-service/user.service.types';
import { ReactQueryKeys } from '@/utils/react-query-keys';

/** Polled: a transfer makes a salesperson busy, and "available" expires on its own. */
export const useSalespeople = (companyId?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ReactQueryKeys.SALESPEOPLE(companyId),
    queryFn: () => getSalespeople(companyId!),
    enabled: !!companyId,
    refetchInterval: OUTBOUND_POLL_INTERVAL_MS,
  });

  return { salespeople: data?.salespeople ?? [], isLoading, error };
};

const useInvalidateSalespeople = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ReactQueryKeys.SALESPEOPLE_ALL() });
};

export const useCreateSalesAgent = (companyId?: number) => {
  const invalidate = useInvalidateSalespeople();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (payload: SalesAgentPayload) => createSalesAgent(companyId!, payload),
    onSuccess: invalidate,
  });

  return { createSalesAgent: mutateAsync, isPending };
};

export const useUpdateSalesAgent = () => {
  const invalidate = useInvalidateSalespeople();

  const { mutateAsync, isPending, variables } = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SalesAgentPayload }) =>
      updateSalesAgent(id, payload),
    onSuccess: invalidate,
  });

  return {
    updateSalesAgent: mutateAsync,
    isPending,
    pendingId: isPending ? variables?.id : undefined,
  };
};

export const useDeleteSalesAgent = () => {
  const invalidate = useInvalidateSalespeople();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: number) => deleteSalesAgent(id),
    onSuccess: invalidate,
  });

  return { deleteSalesAgent: mutateAsync, isPending };
};

/** The header toggle. It reads `user_data`, so the new status is written there at once. */
export const useSetMyAvailability = () => {
  const queryClient = useQueryClient();
  const invalidateSalespeople = useInvalidateSalespeople();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (status: AvailabilityStatus) => setMyAvailability(status),
    onSuccess: ({ id, status, phone, company_id, status_changed_at }) => {
      queryClient.setQueryData<User>(ReactQueryKeys.USER(), (user) =>
        user ? { ...user, sales_agent: { id, status, phone, company_id, status_changed_at } } : user
      );
      invalidateSalespeople();
    },
  });

  return { setMyAvailability: mutateAsync, isPending };
};
